import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { trackEvent } from '../lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OdsAlert } from './orderly/OdsAlert';
import type { OdsAlertType } from './orderly/OdsAlert';
import { OdsButton } from './orderly/OdsButton';

interface Props {
  title: string;
  description: string;
  details?: Array<{ label: string; value: string; subvalue?: string }>;
  items?: ReactNode[];
  warning?: string;
  warningType?: OdsAlertType;
  confirmLabel?: string;
  disabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({ title, description, details, items, warning, warningType = 'warning', confirmLabel = 'Confirm', disabled = false, onConfirm, onCancel }: Props) {
  const showTopWarning = !description && !!warning;

  const renderNotice = () => {
    if (!warning) return null;
    return <OdsAlert type={warningType} className="items-center">{warning}</OdsAlert>;
  };

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'ConfirmModal',
      status: title,
    });
  }, [title]);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent
        showCloseButton={false}
        className="border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] text-[var(--fg-base)] shadow-[var(--shadow-popout)]"
      >
        <div className="grid min-h-0 gap-4 overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[var(--fg-base)]">{title}</DialogTitle>
            {description && <DialogDescription className="leading-6 text-[var(--fg-base)] opacity-60">{description}</DialogDescription>}
          </DialogHeader>

          {showTopWarning && renderNotice()}

          {details && details.length > 0 && (
            <div className="grid gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface-secondary)] p-3">
              {details.map((detail, i) => (
                <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 text-sm" key={i}>
                  <span className="text-[var(--fg-secondary)]">{detail.label}</span>
                  <span className="min-w-0 text-right">
                    <span className="block break-all font-semibold text-[var(--fg-base)]">{detail.value}</span>
                    {detail.subvalue && <span className="mt-1 block break-all text-xs text-[var(--fg-secondary)]">{detail.subvalue}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}

          {items && items.length > 0 && (
            <ul className="grid gap-1">
              {items.map((item, i) => (
                <li className="rounded-md bg-[var(--bg-surface-secondary)] px-3 py-2 text-sm text-[var(--fg-base)]" key={i}>
                  {item}
                </li>
              ))}
            </ul>
          )}

          {warning && !showTopWarning && renderNotice()}
        </div>

        <DialogFooter>
          <OdsButton variant="secondary" onClick={onCancel}>Cancel</OdsButton>
          <OdsButton disabled={disabled} onClick={onConfirm}>{confirmLabel}</OdsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
